/**
 * The store's Record doors, from the browser (D241; `api-contracts`
 * `record-api.ts`): read, fold into, and forget the signed-in account's
 * Record for a game.
 *
 * @remarks
 * Its own small client rather than three more verbs on `StoreClient`: the
 * Record is not a purchase, the doors answer nothing a checkout does, and
 * the shell reads them in exactly one place (`record-storage.ts`). Every
 * call carries the identity cookie (`credentials: 'include'`) — or, on a
 * creator's domain, the bearer session the fetch handed in carries (D170).
 * A store that will not answer is a refusal, never a throw: the Record is
 * the one thing a game may simply forget.
 *
 * @packageDocumentation
 */

import { recordAnswerSchema, type RecordAppendRequest, type RecordForgetRequest, STORE_RECORD_FORGET_PATH, STORE_RECORD_PATH } from '@orpheus/api-contracts';
import { gameIdSchema, type RecordLayer } from '@orpheus/content-schema';

/** The signed-in account's Record, as the shell asks after it. */
export interface RecordDoorsClient {
	/** The account's Record for a game, or nothing when the store will not say (nobody signed in, unreachable). */
	accountRecord(gameId: string): Promise<undefined | readonly RecordLayer[]>;
	/** Fold layers into the account's Record — a client-run turn's, or a device's merging in. Whether the store kept them. */
	foldIntoAccountRecord(gameId: string, layers: readonly RecordLayer[]): Promise<boolean>;
	/** Forget the account's Record for a game. Whether the store forgot it. */
	forgetAccountRecord(gameId: string): Promise<boolean>;
}

/** What the client needs from its edge. */
export interface RecordDoorsClientOptions {
	/** The store Worker's origin — `VITE_STORE_URL`, without a trailing slash. */
	readonly baseUrl: string;
	readonly fetchImplementation?: typeof fetch;
}

/**
 * The client.
 *
 * @param options - {@link RecordDoorsClientOptions}
 * @returns The client
 */
export const createRecordDoorsClient = (options: RecordDoorsClientOptions): RecordDoorsClient => {
	const fetchImplementation = options.fetchImplementation ?? fetch.bind(globalThis);

	/** One call, with the cookie along: the answer's body when the store answered, nothing when it refused or could not be reached. */
	const call = async (path: string, body?: RecordAppendRequest | RecordForgetRequest): Promise<{ readonly answer: unknown } | undefined> => {
		try {
			const response = await fetchImplementation(`${options.baseUrl}${path}`, {
				...(body === undefined ? {} : { body: JSON.stringify(body) }),
				credentials: 'include',
				headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
				method: body === undefined ? 'GET' : 'POST',
			});

			return response.ok ? { answer: await response.json() } : undefined;
		} catch {
			return undefined;
		}
	};

	return {
		accountRecord: async (gameId) => {
			const outcome = await call(`${STORE_RECORD_PATH}?${new URLSearchParams({ gameId }).toString()}`);
			const parsed = recordAnswerSchema.safeParse(outcome?.answer);

			return parsed.success ? parsed.data.layers : undefined;
		},
		foldIntoAccountRecord: async (gameId, layers) => (await call(STORE_RECORD_PATH, { gameId: gameIdSchema.parse(gameId), layers })) !== undefined,
		forgetAccountRecord: async (gameId) => (await call(STORE_RECORD_FORGET_PATH, { gameId: gameIdSchema.parse(gameId) })) !== undefined,
	};
};
